import React from "react";
import { ButtonComponent, GoBack } from "@/components";
import { QRCodeCanvas } from "qrcode.react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiResponse } from "@/types/Api.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import { DepositWalletQueryKey } from "@/config/constants/QueryKeys.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesPathList } from "@/config/routes/Routes.tsx";
import { AxiosError, isAxiosError } from "axios";
import { FormError, ValidationErrors } from "@/types/ValidationError.tsx";
import { formatError } from "@/utils/formatError.tsx";
import { isSNS } from "@/utils/utils";
import bgSNS from "../assets/SVG/bgSNS.svg";
import bgWallet2 from "../assets/SVG/bg_WalletSNS2.svg";

export type WalletAddress = {
  id: number;
  name: string;
  currency: string;
  network: string;
  address: string;
};

type LocationData = {
  currency: string;
  transaction_id: string;
};

const ScanWallet: React.FC = () => {
  const [selectedNetwork, setSelectedNetwork] = React.useState<string>("-");

  const location = useLocation();
  const state = location.state as LocationData;

  const queryClient = useQueryClient();

  const [axiosError, setError] = React.useState<{
    type: "manual";
    message: string;
  } | null>(null);

  const route =
    state?.currency === "BTC"
      ? `${ApiRoutes.DEPOSIT_WALLET}?currency=${state?.currency}`
      : `${ApiRoutes.DEPOSIT_WALLET}?currency=${state?.currency}&network=${selectedNetwork}`;

  const {
    data: wallet,
    isLoading,
    error,
  } = useQuery<ApiResponse<WalletAddress>>({
    queryFn: async () => {
      const response = await GenericApi.get<WalletAddress>(route);

      console.log("Wallet response:", response);

      if (response.error) {
        handleAxiosError(response.error);
        throw response.error;
      }
      return response;
    },
    queryKey: [DepositWalletQueryKey, state?.currency, selectedNetwork],
    enabled: !!state?.currency && state?.currency !== "-",
  });

  console.log("Currency value:", state?.currency);

  const walletAddress = wallet?.data?.address || "";

  const navigate = useNavigate();

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    if (
      isAxiosError<ValidationErrors>(error) &&
      Array.isArray(error.response?.data?.detail)
    ) {
      setError({
        type: "manual",
        message: formatError(error, "An error occurred while registering"),
      });
      return;
    }

    if (isAxiosError<FormError>(error)) {
      setError({
        type: "manual",
        message:
          error.response?.data?.detail ?? "An error occurred while registering",
      });
      return;
    }

    setError({
      type: "manual",
      message: error?.message ?? "An error occurred while registering",
    });
  };

  return !isSNS() ? (
    <div className="overflow-hidden min-h-screen flex w-full bg-[#F3F3F3] pb-[2.6rem] flex-col justify-between items-center">
      <GoBack className={"absolute left-5 top-5 z-50"} fill={"white"} />

      <div className="flex-1 bg-[#202a43] w-full flex justify-center items-center">
        <div
          className={`${
            !walletAddress ? "blur-md" : ""
          } transition duration-300`}
        >
          <QRCodeCanvas
            className="mt-[4.5rem]"
            value={walletAddress || "https://leonext.exchange/"}
            size={300}
            bgColor={"#202a43"}
            fgColor={"#ffffff"}
          />
        </div>
      </div>

      <div className="flex-1">
        <p className="text-[#202a43] text-center text-[1.5rem] mt-5 font-[500]">
          Scan wallet address
        </p>
        {state?.currency !== "BTC" ? (
          <select
            className="w-full p-3 mt-[1rem] border border-[#D7D7D7] rounded-[0.7rem] text-black bg-white appearance-none"
            value={selectedNetwork}
            onChange={(e) => {
              setSelectedNetwork(e.target.value);
              queryClient.invalidateQueries({
                queryKey: [
                  DepositWalletQueryKey,
                  selectedNetwork,
                  state?.currency,
                ],
              });
            }}
          >
            <option value="-" disabled>
              Select network
            </option>
            <option value="ERC20">ERC20</option>
            <option value="TRC20">TRC20</option>
          </select>
        ) : null}

        {!isLoading ? (
          <p className="text-center mt-3">
            {walletAddress.slice(0, 21)}
            <br />
            {walletAddress.slice(21)}
          </p>
        ) : null}

        {isLoading ? (
          <p className="mt-4 text-center text-black">
            Loading your wallet address...
          </p>
        ) : null}

        {axiosError || wallet?.error || error ? (
          <p className="mt-4 text-red-500 text-center">
            {axiosError
              ? axiosError?.message
              : wallet?.error?.message
              ? wallet?.error?.message
              : "Something went wrong"}
          </p>
        ) : null}
      </div>

      <ButtonComponent
        title={"Send for approval"}
        canClick={!!walletAddress}
        actionCallback={() =>
          navigate(RoutesPathList.WaintingApproval, {
            state: {
              transaction_id: state?.transaction_id,
            },
          })
        }
        name={`button--classic w-[13.4375rem] text-center !bg-[#222b45] !text-white text-[1.1rem]`}
      />
    </div>
  ) : (
    <div
      className="overflow-hidden min-h-screen font-[helvetica] w-full bg-[#1C1C1C] pb-[2.6rem] flex flex-col justify-between items-center"
      style={{
        backgroundImage: `url(${bgSNS})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      <GoBack className={"absolute left-5 top-5 z-50"} fill={"#8AFEFD"} />

      <div
        className="relative flex-1 w-full mt-[5rem] flex justify-center items-center pb-[6rem] bg-no-repeat bg-contain"
        style={{
          backgroundImage: `url(${bgWallet2})`,
        }}
      >
        <div
          className={`${
            !walletAddress ? "blur-md" : ""
          } transition duration-300`}
        >
          <QRCodeCanvas
            className="mt-[4.5rem]"
            value={walletAddress || "https://leonext.exchange/"}
            size={250}
            bgColor={""}
            fgColor={"#ffffff"}
          />
        </div>
      </div>

      <div className="flex-1">
        <p className="text-[#fff] text-center text-[1.5rem] mt-5 font-[500]">
          Scan wallet address
        </p>
        {state?.currency !== "BTC" ? (
          <select
            className="w-full p-3 mt-[1rem] border border-[#D7D7D7] rounded-[0.7rem] text-black bg-white appearance-none"
            value={selectedNetwork}
            onChange={(e) => {
              setSelectedNetwork(e.target.value);
              queryClient.invalidateQueries({
                queryKey: [
                  DepositWalletQueryKey,
                  selectedNetwork,
                  state?.currency,
                ],
              });
            }}
          >
            <option value="-" disabled>
              Select network
            </option>
            <option value="ERC20">ERC20</option>
            <option value="TRC20">TRC20</option>
          </select>
        ) : null}

        {!isLoading ? (
          <p className="text-center mt-3 text-white">
            {walletAddress.slice(0, 21)}
            <br />
            {walletAddress.slice(21)}
          </p>
        ) : null}

        {isLoading ? (
          <p className="mt-4 text-center text-white">
            Loading your wallet address...
          </p>
        ) : null}

        {axiosError || wallet?.error || error ? (
          <p className="mt-4 text-red-500 text-center">
            {axiosError
              ? axiosError?.message
              : wallet?.error?.message
              ? wallet?.error?.message
              : "Something went wrong"}
          </p>
        ) : null}
      </div>

      <ButtonComponent
        title={"Send for approval"}
        canClick={!!walletAddress}
        actionCallback={() =>
          navigate(RoutesPathList.WaintingApproval, {
            state: {
              transaction_id: state?.transaction_id,
            },
          })
        }
        name={`button__green w-[14.9375rem] !h-[3.5625rem] !mt-[4rem] !bg-[#8AFEFD] !text-[black] font-[helvetica] !font-[500]`}
      />
    </div>
  );
};

export default ScanWallet;
